import { SupplierCostDocumentCard } from './SupplierCostDocumentCard';
import type { SupplierCostDocumentView } from '../types';

interface SupplierCostDocumentGridProps {
  documents: SupplierCostDocumentView[];
  onDocumentClick?: (document: SupplierCostDocumentView) => void;
  onDelete?: (document: SupplierCostDocumentView) => void;
  emptyMessage?: string;
  showDeleteButton?: boolean;
  isLoading?: boolean;
}

export function SupplierCostDocumentGrid({
  documents,
  onDocumentClick,
  onDelete,
  emptyMessage = 'No supplier cost documents yet',
  showDeleteButton = false,
  isLoading = false,
}: SupplierCostDocumentGridProps) {
  // Loading skeleton
  if (isLoading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="animate-pulse">
            <div className="aspect-square bg-secondary-200 rounded-lg" />
            <div className="mt-2 space-y-2">
              <div className="h-4 bg-secondary-200 rounded w-3/4" />
              <div className="h-3 bg-secondary-200 rounded w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Empty state
  if (documents.length === 0) {
    return (
      <div className="text-center py-12 bg-secondary-50 rounded-lg">
        <svg
          className="w-12 h-12 mx-auto text-secondary-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z"
          />
        </svg>
        <p className="mt-2 text-secondary-600">{emptyMessage}</p>
        <p className="mt-1 text-sm text-secondary-500">
          Receipts and invoices from suppliers will appear here
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
      {documents.map((document) => (
        <SupplierCostDocumentCard
          key={document.id}
          document={document}
          onClick={() => onDocumentClick?.(document)}
          onDelete={() => onDelete?.(document)}
          showDeleteButton={showDeleteButton}
        />
      ))}
    </div>
  );
}
